/**
 * Closed enums for domain values. Single source of truth — backend, frontend,
 * and database (via codegen) all derive from here.
 *
 * Values are stable string identifiers. Display labels are localized via i18n
 * keys named `enum.<enumName>.<value>` (rendered at the UI layer, never
 * stored). Do not rename values without a migration.
 */

import { z } from 'zod';

// ─── User & account ───────────────────────────────────────────────────────

export const Gender = z.enum(['male', 'female']);
export type Gender = z.infer<typeof Gender>;

export const UserStatus = z.enum([
  'pending_phone_verification',
  'pending_profile',
  'pending_liveness',
  'active',
  'paused_by_user',
  'paused_by_admin',
  'banned',
  'deletion_pending',
  'deleted',
]);
export type UserStatus = z.infer<typeof UserStatus>;

export const AuthProvider = z.enum(['google', 'facebook', 'apple', 'email_password']);
export type AuthProvider = z.infer<typeof AuthProvider>;

// ─── Religion ─────────────────────────────────────────────────────────────

export const Sect = z.enum(['sunni', 'shia']);
export type Sect = z.infer<typeof Sect>;

/**
 * Sub-sect / school of jurisprudence. Closed enum (no free text) — the UI is
 * a dropdown so spelling variants ("Hanafee" vs "Hanafi") don't shard the
 * data. `na` is the explicit "doesn't apply / I don't identify with a
 * sub-sect" answer; `other` is the escape hatch for schools we don't
 * enumerate (Alawite, Khoja sub-categories, etc.). Sub-sect is informational
 * at MVP — `Sect` remains the hard-filter axis.
 *
 * Curation rationale:
 *   - 4 Sunni madhhabs (Hanafi, Shafi'i, Maliki, Hanbali) — orthodox
 *     jurisprudence schools covering Sunni-majority launch markets.
 *   - 3 Shia branches (Twelver, Ismaili, Zaidi) — covers Iran/Iraq/Lebanon
 *     diaspora + Khoja/Bohra communities + Yemen.
 *   - Ibadi — distinct from both, dominant in Oman (launch market).
 *   - Sufi + Salafi — movements that cross-cut madhhab lines; users
 *     self-identify with them as a primary label often.
 *   - Ahmadi is intentionally excluded (legally classified as non-Muslim
 *     in Pakistan and elsewhere; including would be religiously
 *     contentious in matrimonial context). Users can choose `other`.
 */
export const SubSect = z.enum([
  'na',
  'hanafi',
  'shafii',
  'maliki',
  'hanbali',
  'twelver',
  'ismaili',
  'zaidi',
  'ibadi',
  'sufi',
  'salafi',
  'other',
]);
export type SubSect = z.infer<typeof SubSect>;

export const PrayerFrequency = z.enum(['5x_daily', 'mostly', 'sometimes', 'rarely', 'never']);
export type PrayerFrequency = z.infer<typeof PrayerFrequency>;

export const FastingFrequency = z.enum(['always', 'mostly', 'sometimes', 'rarely', 'never']);
export type FastingFrequency = z.infer<typeof FastingFrequency>;

export const QuranEngagement = z.enum([
  'daily',
  'weekly',
  'monthly',
  'sometimes',
  'rarely',
  'never',
]);
export type QuranEngagement = z.infer<typeof QuranEngagement>;

export const DressStyleWoman = z.enum([
  'niqab',
  'jilbab',
  'hijab',
  'modest_no_hijab',
  'casual',
  'dont_dress_religiously',
  'prefer_not_say',
]);
export type DressStyleWoman = z.infer<typeof DressStyleWoman>;

export const DressStyleMan = z.enum([
  'thobe_regular',
  'thobe_occasional',
  'beard_full',
  'beard_trim',
  'casual',
  'dont_dress_religiously',
  'prefer_not_say',
]);
export type DressStyleMan = z.infer<typeof DressStyleMan>;

export const Diet = z.enum([
  'halal_strict',
  'halal_flexible',
  'vegetarian',
  'vegan',
  'pescatarian',
  'no_restriction',
]);
export type Diet = z.infer<typeof Diet>;

export const AlcoholHabit = z.enum(['never', 'quit', 'sometimes', 'regular']);
export type AlcoholHabit = z.infer<typeof AlcoholHabit>;

export const SmokingHabit = z.enum(['never', 'ex', 'sometimes', 'regular']);
export type SmokingHabit = z.infer<typeof SmokingHabit>;

export const ConvertStatus = z.enum(['born_muslim', 'revert']);
export type ConvertStatus = z.infer<typeof ConvertStatus>;

// ─── Islamic-practice depth (matrimonial-polish audit) ────────────────
//
// Optional self-disclosure axes that make the profile read matrimonial
// rather than dating-derived. All fields default to `prefer_not_to_say`
// so the wizard step is fully skippable; a user who picks any value
// gets a structured signal on their profile that the matcher can use
// as a soft re-rank later (no hard filter at MVP — these are signals,
// not gates).
//
// Plan §matrimonial-polish PR #2.

/** Whether the user has completed Hajj. The "intend" values let
 *  young users signal their plan without overclaiming. `with_family`
 *  is a common pattern in some MENA / South Asian markets (Hajj
 *  delegated to parents on the user's behalf via badal). */
export const HajjStatus = z.enum([
  'prefer_not_to_say',
  'completed',
  'planned_soon',
  'intend_someday',
  'with_family',
  'not_intending',
]);
export type HajjStatus = z.infer<typeof HajjStatus>;

/** Umrah — same shape as Hajj but more frequent; `multiple` is
 *  a meaningful disclosure for many practicing users. */
export const UmrahStatus = z.enum([
  'prefer_not_to_say',
  'multiple',
  'completed',
  'planned_soon',
  'intend_someday',
  'not_yet',
]);
export type UmrahStatus = z.infer<typeof UmrahStatus>;

/** Quranic memorization (hifz). Closed enum for matchable signal. */
export const QuranMemorization = z.enum([
  'prefer_not_to_say',
  'none',
  'partial_juz',
  'multiple_ajza',
  'full_hifz',
]);
export type QuranMemorization = z.infer<typeof QuranMemorization>;

/** Mosque attendance frequency. Distinct from `prays` (which is
 *  about the five daily salah anywhere) and `quranEngagement` (which
 *  is reading practice). Mosque attendance captures the community-
 *  participation dimension which many matrimonial users care about
 *  even when prayer routine is private. */
export const MosqueAttendance = z.enum([
  'prefer_not_to_say',
  'regularly',
  'friday_only',
  'occasionally',
  'eid_only',
  'not_currently',
]);
export type MosqueAttendance = z.infer<typeof MosqueAttendance>;

// ─── Marital + life context ───────────────────────────────────────────────

export const MaritalStatus = z.enum(['never_married', 'divorced', 'widowed']);
export type MaritalStatus = z.infer<typeof MaritalStatus>;

/**
 * Audit Spec C5 — richer children-custody texture.
 *
 * The original 4-value version (full / partial / none / not_applicable)
 * lost the texture that matrimonial matches actually probe. South Asian
 * / MENA divorced-parent profiles in particular benefit from being able
 * to disclose practical custody arrangements honestly (which weeks the
 * kids are with you, whether the other parent is in the country, etc.)
 * because that's what shapes a future household.
 *
 *   - lives_with_me        primary residence with you
 *   - shared_custody       truly shared (alternating weeks / equal time)
 *   - weekend_or_holiday   you have them weekends / school holidays
 *   - international        other parent in a different country
 *   - other_parent         other parent has primary custody
 *   - prefer_not_to_say    sensitive — explicit default
 *
 * Only rendered in the UI when `hasChildren = true`.
 */
export const ChildrenCustody = z.enum([
  'lives_with_me',
  'shared_custody',
  'weekend_or_holiday',
  'international',
  'other_parent',
  'prefer_not_to_say',
]);
export type ChildrenCustody = z.infer<typeof ChildrenCustody>;

export const EducationLevel = z.enum([
  'high_school',
  'bachelors',
  'masters',
  'phd',
  'vocational',
  'other',
]);
export type EducationLevel = z.infer<typeof EducationLevel>;

/**
 * Industry — coarser bucket than the free-form `profession` string.
 * Helps the matcher re-rank and gives users a useful filter axis in
 * South Asian / MENA markets where industry strata predict
 * compatibility better than specific job titles. Optional disclosure;
 * `prefer_not_to_say` is the explicit default.
 *
 * Matrimonial-polish audit PR #4.
 */
export const Industry = z.enum([
  'prefer_not_to_say',
  'healthcare',
  'engineering_tech',
  'business_finance',
  'government_policy',
  'education_academia',
  'creative_media',
  'legal',
  'trades_crafts',
  'science_research',
  'religious_education',
  'homemaker',
  'student',
  'between_roles',
  'other',
]);
export type Industry = z.infer<typeof Industry>;

/**
 * Employment status — captures "currently supports family" /
 * "still studying" / "between roles" with more dignity than burying
 * it inside the free-form profession string. `homemaker` is a
 * deliberate first-class value (matrimonial-coded, not coded as
 * lower-status; both genders can be homemakers).
 */
export const EmploymentStatus = z.enum([
  'prefer_not_to_say',
  'employed',
  'self_employed',
  'business_owner',
  'student',
  'homemaker',
  'between_roles',
]);
export type EmploymentStatus = z.infer<typeof EmploymentStatus>;

// ─── Marriage intent ──────────────────────────────────────────────────────

export const MarriageTimeframe = z.enum(['within_6mo', '6_to_12mo', '1_to_2yr', 'exploring']);
export type MarriageTimeframe = z.infer<typeof MarriageTimeframe>;

export const FamilyInvolvement = z.enum([
  'immediately',
  'after_we_connect',
  'after_we_meet',
  'when_we_both_agree',
  'case_by_case',
]);
export type FamilyInvolvement = z.infer<typeof FamilyInvolvement>;

export const WantsChildren = z.enum(['definitely', 'wants', 'open', 'no']);
export type WantsChildren = z.infer<typeof WantsChildren>;

export const Relocation = z.enum(['will', 'wont', 'negotiable']);
export type Relocation = z.infer<typeof Relocation>;

/**
 * Where the couple intends to live post-marriage. Symmetric enum (no
 * "his side / her side" split) so mutual-compatibility matching is a
 * straightforward set-intersection on `livingArrangementAcceptable`.
 * Which family specifically (his / hers / both) is a conversation
 * topic, not a filter.
 */
export const LivingArrangement = z.enum([
  'own_home',
  'with_parents',
  'extended_family',
  'negotiable',
]);
export type LivingArrangement = z.infer<typeof LivingArrangement>;

/**
 * Polygamy preference. Halal in Islam under specific conditions; a
 * meaningful filter for users on either side of the question. Kept
 * gender-neutral at the enum level; the UI / i18n layer can phrase
 * "open" as "open to being a co-wife" for women, "open to taking
 * another wife" for men. `currently_married` lets a man with an
 * existing wife declare it upfront — the recipient sees this in the
 * profile, no surprises mid-conversation. `prefer_not_to_say` is the
 * privacy escape hatch.
 */
export const PolygamyPreference = z.enum([
  'monogamy_only',
  'open',
  'negotiable',
  'currently_married',
  'prefer_not_to_say',
]);
export type PolygamyPreference = z.infer<typeof PolygamyPreference>;

/**
 * Audit Spec C7 — first-class deal-breaker tags.
 *
 * Each tag in the user's preferences excludes candidates whose traits
 * match the corresponding rule. Designed deliberately small + opinionated:
 * deal-breakers should be RARE filter axes that don't already have a
 * dedicated mutual-preference field, otherwise the wide acceptable-sets
 * would double-gate the same thing.
 *
 * The trait mappings live in `dealBreakerExcludes()` (helpers) so the
 * wire format (this enum) stays decoupled from candidate-shape logic.
 *
 * Pre-launch the list is six values — covers the plan's hint set without
 * expanding into territory better handled by full filter axes.
 */
export const DealBreakerTag = z.enum([
  'smokes',
  'drinks_alcohol',
  'wants_polygamy',
  'doesnt_pray',
  'has_children',
  'previously_married',
]);
export type DealBreakerTag = z.infer<typeof DealBreakerTag>;

/**
 * Audit Spec E7 — unmatch-reason capture.
 *
 * Tiny optional picker on the unmatch confirmation. Values mirror the
 * audit's listed set: not aligned / no spark / off-platform / safety
 * concern. Stored on `matches.unmatch_reason`; audit-only (the other
 * party never sees what was picked — they just see the match end).
 *
 * Why so few values: the picker has to be DOA-2-second-fast for the
 * user who's just decided to disengage. Each option here maps to a
 * distinct moderation signal worth surfacing in the admin queue
 * (especially `safety_concern`, which becomes a soft signal even
 * without a formal report).
 */
export const UnmatchReason = z.enum(['not_aligned', 'no_spark', 'off_platform', 'safety_concern']);
export type UnmatchReason = z.infer<typeof UnmatchReason>;

// ─── Financial readiness + family background (audit Spec C2/C3/C4) ────────

/**
 * Income bucket — vague on purpose; no exact figures. Labels rendered
 * to the user are localised per market (e.g. "comfortable" is a
 * different number in Karachi vs London). The enum value is stable
 * across markets — same key, market-aware label at the UI layer.
 *
 * Default `prefer_not_to_say` respects users (especially women) for
 * whom income disclosure is sensitive. Display gated by the
 * mutual-disclosure rule in profile-service's public-profile route.
 */
export const IncomeBracket = z.enum([
  'prefer_not_to_say',
  'building_career',
  'stable',
  'comfortable',
  'well_above_average',
]);
export type IncomeBracket = z.infer<typeof IncomeBracket>;

/**
 * Mahr expectation. Sensitive — many women won't disclose, hence
 * `prefer_not_to_say` is the explicit default. The bio prompts spec
 * (B1/C1) includes an optional "What are your mahr expectations?"
 * prompt for users who want to talk about it in their own words.
 */
export const MahrExpectation = z.enum([
  'prefer_not_to_say',
  'traditional',
  'modest',
  'open_to_discuss',
]);
export type MahrExpectation = z.infer<typeof MahrExpectation>;

/**
 * Family practice level. Captures the household religious context a
 * future spouse would be marrying into — distinct from the user's
 * own religiosity. PK/IN/MENA families often probe this early; the
 * platform surfaces it as a structured field so it isn't lost in bio.
 */
export const FamilyPractice = z.enum([
  'prefer_not_to_say',
  'very_religious',
  'religious',
  'moderately_practicing',
  'cultural_muslim',
]);
export type FamilyPractice = z.infer<typeof FamilyPractice>;

/**
 * Coarse signal about family education norms — mostly used by South
 * Asian / MENA families where biradari / education strata predict
 * compatibility better than individual education alone.
 */
export const FamilyEducationLevel = z.enum([
  'prefer_not_to_say',
  'mixed_secondary',
  'mostly_university',
  'mostly_postgrad',
]);
export type FamilyEducationLevel = z.infer<typeof FamilyEducationLevel>;

/**
 * Where the user lives relative to family. Distinct from
 * `living_arrangement` (which is the user's preference for the
 * marital household — that's about the future, this is about now).
 */
export const FamilyResidence = z.enum([
  'prefer_not_to_say',
  'with_parents',
  'extended_family_close',
  'parents_abroad',
  'independent',
]);
export type FamilyResidence = z.infer<typeof FamilyResidence>;

// ─── Search / matching ────────────────────────────────────────────────────

export const SearchMode = z.enum(['distance_only', 'countries_only', 'distance_or_countries']);
export type SearchMode = z.infer<typeof SearchMode>;

// ─── Interactions ─────────────────────────────────────────────────────────

export const InteractionAction = z.enum([
  'pass',
  'like',
  'approached_free',
  'approached_paid',
  'ignored_received',
  'dismissed_received',
  'blocked_received',
  'liked_back',
]);
export type InteractionAction = z.infer<typeof InteractionAction>;

// ─── Reports ──────────────────────────────────────────────────────────────

export const ReportReason = z.enum([
  'inappropriate_photo',
  'fake_profile',
  'harassment',
  'scammer_or_spam',
  'underage',
  'married_or_misrepresenting',
  'hate_speech',
  'other',
]);
export type ReportReason = z.infer<typeof ReportReason>;

export const ReportStatus = z.enum(['open', 'withdrawn', 'dismissed', 'confirmed']);
export type ReportStatus = z.infer<typeof ReportStatus>;

// ─── Photos ───────────────────────────────────────────────────────────────

export const PhotoModerationStatus = z.enum(['pending', 'approved', 'rejected']);
export type PhotoModerationStatus = z.infer<typeof PhotoModerationStatus>;

/**
 * Profile voice / video intro moderation status (matrimonial-polish PR #5).
 *
 * Same lifecycle as photos but on independent recordings. `pending`
 * is the initial state after upload; the moderation worker flips it
 * to `approved` (visible) or `rejected` (hidden, user prompted to
 * re-record). At MVP the worker auto-approves in dev and stages a
 * Bedrock/Rekognition pipeline in prod (gated by the
 * `features.profileIntros` flag — not wired live until moderation
 * is real).
 */
export const ProfileIntroModerationStatus = z.enum(['pending', 'approved', 'rejected']);
export type ProfileIntroModerationStatus = z.infer<typeof ProfileIntroModerationStatus>;

/** Profile-intro media type discriminator. */
export const ProfileIntroKind = z.enum(['voice', 'video']);
export type ProfileIntroKind = z.infer<typeof ProfileIntroKind>;

// ─── Subscriptions ────────────────────────────────────────────────────────

export const SubscriptionTier = z.enum(['bronze', 'silver', 'gold']);
export type SubscriptionTier = z.infer<typeof SubscriptionTier>;

export const SubscriptionInterval = z.enum(['month', 'six_month', 'year']);
export type SubscriptionInterval = z.infer<typeof SubscriptionInterval>;

export const SubscriptionStatus = z.enum([
  'trialing',
  'active',
  'paused',
  'cancelled',
  'past_due',
  'incomplete',
]);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatus>;

export const SubscriptionSource = z.enum(['web_stripe', 'ios_iap', 'android_iap']);
export type SubscriptionSource = z.infer<typeof SubscriptionSource>;

export const SubscriptionPerk = z.enum([
  'ad_free',
  'who_liked_you',
  'who_visited_you',
  // `incognito_browse` REMOVED from tier perks 2026-06-02 (not
  // matrimonial-respectful). The enum value is preserved so any
  // legacy DB rows holding it as a string still parse — but no
  // tier grants it, the view-recorder no longer respects it, and
  // no UI surfaces it. Don't add it to new tiers.
  'incognito_browse',
  'advanced_filters',
  'read_receipts',
  'weekly_boost',
  'priority_moderation',
]);
export type SubscriptionPerk = z.infer<typeof SubscriptionPerk>;

// ─── Credits ──────────────────────────────────────────────────────────────

export const CreditReason = z.enum([
  'purchase',
  'subscription_grant',
  'signup_bonus',
  'paid_approach',
  'refund',
  'trial_clawback',
  'admin_grant',
  'admin_revoke',
  'referral_reward',
]);
export type CreditReason = z.infer<typeof CreditReason>;

export const CreditPack = z.enum(['pack_1', 'pack_10', 'pack_25']);
export type CreditPack = z.infer<typeof CreditPack>;

// ─── Consent ──────────────────────────────────────────────────────────────

export const ConsentType = z.enum([
  'terms_of_service',
  'privacy_policy',
  'marketing_email',
  'ad_personalization',
  'analytics',
]);
export type ConsentType = z.infer<typeof ConsentType>;

// ─── Marital-history depth (audit Spec J2 — divorced/widowed) ─────────────

/**
 * Where the user's children live now. Distinct from `hasChildren`
 * (binary) and `childrenCount` (number) — captures the practical
 * custody picture that matters for cross-marriage planning.
 */
export const ChildrenLivingSituation = z.enum([
  'with_me_full_time',
  'with_me_part_time',
  'with_other_parent',
  'independent',
  'prefer_not_to_say',
]);
export type ChildrenLivingSituation = z.infer<typeof ChildrenLivingSituation>;

/**
 * The pace at which the user is seeking — used to calibrate the
 * re-engagement notification cadence (widowed users in their first
 * 6 months get a softer cadence; "actively_seeking" users get the
 * standard cadence). Subjective; user controls it.
 *
 * Three values lean never-married / divorced; three lean widowed
 * (the "ready_when_right" variant softens the framing). UI picker
 * shows the appropriate three based on the user's marital_status.
 */
export const SeekingPace = z.enum([
  'ready_now',
  'taking_my_time',
  'looking_thoughtfully',
  'ready_when_right',
  'not_in_rush',
  'actively_seeking',
]);
export type SeekingPace = z.infer<typeof SeekingPace>;

/**
 * Why a viewer passed on a candidate (audit Spec D2).
 *
 * Optional, captured AFTER the pass via the second-look toast — never
 * blocks the pass action. Stored on the `interactions.pass_reason`
 * column. Used (eventually) by match-service to soften specific axes
 * for that viewer + this segment of candidates, and surfaces in the
 * user's own activity history so they can see their own filter
 * pattern over time.
 *
 * Tone: dignified, not bitter. No "ghost," "not interested," "too
 * old," "too short," etc. — those land as cruelty when summarised in
 * any moderation review. See docs/brand/voice.md.
 */
export const PassReason = z.enum([
  'not_a_match',
  'faith_practice_difference',
  'distance_or_relocation',
  'life_stage',
  'style_difference',
  'just_browsing',
]);
export type PassReason = z.infer<typeof PassReason>;
